import './style/Keystone.css';
import React, { useEffect, useState, useMemo } from 'react';
import { useEvent as useEffectEvent } from '@reactuses/core';
import Menu from '../component/keystone/Menu';
import Tray from '../component/keystone/Tray';
import Content from '../component/keystone/Content';
import Loader from '../component/keystone/Loader';
import ToastWrapper from '../component/keystone/ToastsWrapper';
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { setView, setNotifications, fetchMagicLinkAccessToken, fetchUser, setMagicLinkCode, setAuthRedirectPage } from '../redux/keystoneSlice';
import { selectAllInventoryItemsWithData } from '../redux/keystoneInventorySlice';
import {
    setIsLoggedIn,
    setShowModal,
    setModalType,
    fetchCompany,
    setAccessToken,
    setInviteCode,
    setPayoutsSetup,
    verifyInviteCode,
    fetchInventory,
    fetchShipments,
    fetchTeam,
    fetchPayouts,
    fetchOffers,
    fetchBankAccounts,
    setTemporaryProductFound,
    setOperationId,
    setUploadedInventory,
    setErrorUploadingInventory,
    setValidationErrors,
    setCommittedInventory,
    setHeaderMappings,
    setSetWarehouseLocationsSuccessfully,
    setErrorSettingWarehouseLocations,
    setSettingWarehouseLocations,
    submitFeedback,
    setFeedbackSubmitted
} from '../redux/keystoneInterfaceSlice';
import { resetAppState } from '../redux/appReducer';
import Plaid from '../component/keystone/Plaid';
import { Modal } from '@harperai/materialize-design-system';
import BoxMinimumModal from '../component/keystone/BoxMinimumModal';
import InviteModal from '../component/keystone/InviteModal';
import SetWarehouseLocationModal from '../component/keystone/SetWarehouseLocationModal';
import ConfirmModal from '../component/keystone/ConfirmModal';
import TextAreaModal from '../component/keystone/TextAreaModal';
import TermsAgreement from '../component/keystone/TermsAgreement';
import UploadFlow from '../component/keystone/UploadFlow';
import CreateAccount from '../component/keystone/CreateAccount';

const loadingTextOptions = [
    "Looking up the part...",
    "Gathering price data...",
    "Checking stock levels...",
    "Assembling product page..."
];

const Keystone = () => {
    const dispatch = useDispatch();

    // state.keystone
    const keystoneMagicLinkCode = useSelector((state) => state?.keystone?.magicLinkCode);
    const keystoneAuthRedirectPage = useSelector((state) => state?.keystone?.auth?.redirectPage);
    const keystoneNotifications = useSelector((state) => state?.keystone?.notifications);
    const keystoneTemporaryAccessToken = useSelector((state) => state?.keystone?.temporaryAccessToken);
    const keystoneErrorFetchingAccessToken = useSelector((state) => state?.keystone?.errorFetchingAccessToken);

    // state.keystoneInventory
    const keystoneInventoryBankAccounts = useSelector((state) => state?.keystoneInventory?.bank_accounts);
    const keystoneInventoryAll = useSelector(selectAllInventoryItemsWithData);

    // state.keystoneInterface
    const keystoneInterfaceSearchingMpn = useSelector((state) => state?.keystoneInterface?.searchingMpn);
    const keystoneInterfaceHasFetchedInventory = useSelector((state) => state?.keystoneInterface?.hasFetchedInventory);
    const keystoneInterfaceOperationId = useSelector((state) => state?.keystoneInterface?.operation_id);
    const keystoneInterfaceTemporaryProductFound = useSelector((state) => state?.keystoneInterface?.temporaryProductFound);
    const keystoneInterfaceErrorSearchingMpn = useSelector((state) => state?.keystoneInterface?.errorSearchingMpn);
    const keystoneInterfaceErrorFetchingInventory = useSelector((state) => state?.keystoneInterface?.errorFetchingInventory);
    const keystoneInterfaceInviteCode = useSelector((state) => state?.keystoneInterface?.inviteCode);
    const keystoneInterfaceAccessToken = useSelector((state) => state?.keystoneInterface?.accessToken);
    const keystoneInterfaceInviteVerified = useSelector((state) => state?.keystoneInterface?.inviteVerified);
    const keystoneInterfaceIsLoggedIn = useSelector((state) => state?.keystoneInterface?.isLoggedIn);
    const keystoneInterfaceErrorFetchingUser = useSelector((state) => state?.keystoneInterface?.errorFetchingUser);
    const keystoneInterfaceErrorVerifyingInviteCode = useSelector((state) => state?.keystoneInterface?.errorVerifyingInviteCode);
    const keystoneInterfaceCompanyHasLinkedBankAccounts = useSelector((state) => state?.keystoneInterface?.company?.has_linked_bank_accounts);
    const keystoneInterfaceCompany = useSelector((state) => state?.keystoneInterface?.company);
    const keystoneUser = useSelector((state) => state?.keystone?.user);
    const keystoneHasFetchedUser = useSelector((state) => state?.keystone?.hasFetchedUser);
    const keystoneTosAccepted = useSelector((state) => state?.keystone?.tosAccepted);

    const keystoneInterfaceShowModal = useSelector((state) => state?.keystoneInterface?.showModal);
    const keystoneInterfaceModalType = useSelector((state) => state?.keystoneInterface?.modalType);
    const keystoneInventoryTeamMembers = useSelector((state) => state?.keystoneInventory?.team_members);
    const keystoneUserToChange = useSelector((state) => state?.keystone?.userToChange);
    const keystoneRoleToChange = useSelector((state) => state?.keystone?.roleToChange);
    const keystoneUserToDelete = useSelector((state) => state?.keystone?.userToDelete);
    const keystoneInterfaceSubmittingFeedback = useSelector((state) => state?.keystoneInterface?.submittingFeedback);
    const keystoneInterfaceFeedbackSubmitted = useSelector((state) => state?.keystoneInterface?.feedbackSubmitted);
    
    const navigate = useNavigate();
    const { search, pathname } = useLocation();
    const searchParams = Object.fromEntries(new URLSearchParams(search));
    const { page } = useParams();
    const { isAuthenticated, user, logout, getAccessTokenSilently, loginWithRedirect, isLoading, error } = useAuth0();
    const loader = true;

    const [ showLoader, setShowLoader ] = useState(loader);
    const [menuOpen, setMenuOpen] = useState(true);

    const showLoadingText = keystoneInterfaceSearchingMpn === true;
    const [fontsLoaded, setFontsLoaded] = useState(false);
    const [uploadStep, setUploadStep] = useState(keystoneInterfaceModalType?.includes("upload-flow-file") ? 1 : 0);

    
    useEffect(() => {
        if (document.fonts) {
          document.fonts.ready.then(() => {
            setFontsLoaded(true);
          });
        } else {
            setFontsLoaded(true);
        }
    }, []);

    // Custom login function
    const handleGoogleAccountCreate = async () => {
        dispatch(setAuthRedirectPage(page || 'inventory'));
        return await loginWithRedirect({
            authorizationParams: {
                screen_hint: 'signup',
                connection: 'google-oauth2',
                magic_link_code: keystoneMagicLinkCode,
                prompt: 'select_account',
                scope: `openid profile email offline_access magic_link_code:${keystoneMagicLinkCode}`
            }
        });
    };

    const handleGoogleAccountCreateFromInvite = async () => {
        dispatch(setAuthRedirectPage('inventory')); // go to inventory after invite
        return await loginWithRedirect({
            authorizationParams: {
                screen_hint: 'signup',
                connection: 'google-oauth2',
                invite_code: keystoneInterfaceInviteCode,
                scope: `openid profile email offline_access invite_code:${keystoneInterfaceInviteCode}`,
                prompt: 'select_account'
            }
        });
    };

    const handleCredentialAccountCreate = async (email) => {
        dispatch(setAuthRedirectPage(page || 'inventory'));
        return await loginWithRedirect({
            authorizationParams: {
                screen_hint: 'signup',
                connection: 'Username-Password-Authentication',
                login_hint: email,
                magic_link_code: keystoneMagicLinkCode,
                full_name: keystoneUser?.first_name && keystoneUser?.last_name ? `${keystoneUser?.first_name} ${keystoneUser?.last_name}` : '',
                company_name: keystoneUser?.company?.name ? keystoneUser?.company?.name : '',
                scope: `openid profile email offline_access magic_link_code:${keystoneMagicLinkCode}`
            },
        });
    };

    const handleCredentialAccountCreateFromInvite = async (email) => {
        dispatch(setAuthRedirectPage('inventory')); // go to inventory after invite
        return await loginWithRedirect({
            authorizationParams: {
                screen_hint: 'signup',
                connection: 'Username-Password-Authentication',
                login_hint: email,
                invite_code: keystoneInterfaceInviteCode,
                scope: `openid profile email offline_access invite_code:${keystoneInterfaceInviteCode}`
            },
        });
    };

    const getAccessToken = useEffectEvent(async () => {
        try {
            const local = window.location.origin.includes('local') ? true : false;

            const opts = {
                authorizationParams: {
                    audience: local ? 'https://dev-s6tdx5li201sdtoo.us.auth0.com/api/v2/' : 'https://trymaterialize.us.auth0.com/api/v2/',
                    scope: "openid profile email offline_access"
                },
            };
        
            let accessToken = await getAccessTokenSilently(opts);
            dispatch(setAccessToken(accessToken));
            if (keystoneAuthRedirectPage) {
                const redirectPage = keystoneAuthRedirectPage;
                dispatch(setAuthRedirectPage(null));
                navigate(`/${redirectPage}`);
            }
        } catch (error) {
            console.log(`Error authenticating: ${error}`);
            handleLogout();
        }
    });

    const handleLogout = useEffectEvent(() => {
        dispatch(resetAppState());
        setShowLoader(true);
        logout();
    });

    const fetchBankAccountData = useEffectEvent(() => {
        dispatch(fetchBankAccounts({ accessToken: keystoneInterfaceAccessToken }));
    });
    
    const navigateToSelectedId = useEffectEvent((id) => {
        navigate(`${pathname}?id=${id}`);
    });
    
    const fetchUserAndCompany = useEffectEvent(() => {
        dispatch(fetchCompany({ accessToken: keystoneInterfaceAccessToken }));
        dispatch(fetchUser({ accessToken: keystoneInterfaceAccessToken }));
    });
    
    if (keystoneNotifications?.find(notification => notification.page === page)) {
        const updatedNotifications = keystoneNotifications?.filter(notification => notification.page !== page);
        dispatch(setNotifications(updatedNotifications));
    }

    useEffect(() => {
        if (page === 'login' && isAuthenticated) {
            console.log("Ignoring magic link, user is already logged in.");
            navigate('/inventory');
        } else if (page === 'login' && searchParams.code && !keystoneInterfaceAccessToken) {
            dispatch(resetAppState());
            dispatch(setMagicLinkCode(searchParams.code));
            dispatch(fetchMagicLinkAccessToken({ code: searchParams.code }));
            setShowLoader(true);
        } else if (page === 'login') {
            navigate('/inventory');
        }
    }, [page, isAuthenticated, searchParams.code, keystoneInterfaceAccessToken, navigate, dispatch]);

    useEffect(() => {
        if (page === 'invite' && !isLoading && !user && !keystoneTemporaryAccessToken && searchParams.code) {
            dispatch(resetAppState());
            dispatch(setInviteCode(searchParams.code));
            dispatch(setView("invite"));
            setShowLoader(true);
            dispatch(setModalType("create-account-invite"));
            dispatch(setShowModal(true));
            dispatch(verifyInviteCode({ code: searchParams.code }));
        } else if (page === 'invite' && !isLoading) {
            console.log("Ignoring invite link as user already has account.");
            navigate('/inventory');
        }
    }, [page, isLoading, user, keystoneTemporaryAccessToken, searchParams.code, dispatch, navigate]);

    useEffect(() => {
        if (keystoneInterfaceInviteVerified) {
            setShowLoader(false);
        }
    }, [keystoneInterfaceInviteVerified]);
    
    useEffect(() => {
        if (keystoneInventoryBankAccounts?.length > 0) {
            dispatch(setPayoutsSetup(true));
        } else {
            dispatch(setPayoutsSetup(false));
        }
    }, [dispatch, keystoneInventoryBankAccounts?.length]);

    useEffect(() => {
        if (user) {
            dispatch(setIsLoggedIn(true));
            setShowLoader(false);
        }
    }, [dispatch, user]);
    
    useEffect(() => {
        if (keystoneInterfaceIsLoggedIn && keystoneHasFetchedUser && !keystoneInterfaceErrorFetchingUser && !keystoneTosAccepted) {
            dispatch(setModalType("tos"));
            dispatch(setShowModal(true));
        } else if (keystoneTosAccepted) {
            dispatch(setShowModal(false));
        }
    }, [keystoneInterfaceIsLoggedIn, keystoneHasFetchedUser, keystoneInterfaceErrorFetchingUser, keystoneTosAccepted, dispatch]);

    useEffect(() => {
        if (keystoneInterfaceAccessToken && keystoneInterfaceHasFetchedInventory && keystoneInventoryAll?.length === 0 && keystoneMagicLinkCode) {    
            dispatch(setShowModal(true));
            dispatch(setModalType("upload-flow"));
        }
    }, [keystoneInterfaceAccessToken, keystoneInterfaceHasFetchedInventory, keystoneInventoryAll?.length, keystoneMagicLinkCode, dispatch]);

    useEffect(() => {
        if (error) {
            console.error(error);
        }
        if (!isLoading && isAuthenticated) {
            getAccessToken();
        }
    }, [isAuthenticated, isLoading, error]);

    useEffect(() => {
        if (keystoneInterfaceErrorVerifyingInviteCode) {
            handleLogout();
        }
    }, [keystoneInterfaceErrorVerifyingInviteCode]);

    useEffect(() => {
        if (!isLoading && page && page !== 'invite' && page !== 'login' && !isAuthenticated && !keystoneTemporaryAccessToken && !keystoneInterfaceAccessToken && !keystoneMagicLinkCode) {
            // Authenticated pages require an access token. Log them out if not logged in.
            handleLogout();
        }
    }, [page, keystoneTemporaryAccessToken, keystoneInterfaceAccessToken, keystoneMagicLinkCode, isAuthenticated, isLoading]);

    useEffect(() => {
        if (!page && (keystoneInterfaceIsLoggedIn || (keystoneTemporaryAccessToken && !isLoading))) {
            navigate('/inventory');
        } else if (!page && isLoading && searchParams.code) {
            setShowLoader(true);
        }
    }, [page, keystoneInterfaceIsLoggedIn, keystoneTemporaryAccessToken, navigate, isLoading, searchParams.code]);

    useEffect(() => {
        if (keystoneInterfaceCompanyHasLinkedBankAccounts) {
            fetchBankAccountData();
        }
    }, [keystoneInterfaceCompanyHasLinkedBankAccounts]);

    
    useEffect(() => {
        if (keystoneInterfaceOperationId) {
            setShowLoader(false);
        }
    }, [dispatch, keystoneInterfaceOperationId]);

    useEffect(() => {
        if (keystoneInterfaceSearchingMpn) {
            setShowLoader(true);
        }
    }, [keystoneInterfaceSearchingMpn]);
    

    useEffect(() => {
        if (keystoneInterfaceTemporaryProductFound && keystoneInventoryAll?.length > 0) {
            dispatch(setShowModal(false));
            setShowLoader(false);
            navigateToSelectedId(keystoneInventoryAll[0]?.id);
            dispatch(setTemporaryProductFound(false));
        }
    }, [keystoneInterfaceTemporaryProductFound, keystoneInventoryAll, dispatch]);

    useEffect(() => {
        if (keystoneInterfaceErrorSearchingMpn) {
            setShowLoader(false);
        }
    }, [keystoneInterfaceErrorSearchingMpn]);

    useEffect(() => {
        if (page && keystoneInterfaceAccessToken) {
            if (page === "shipments") {
                dispatch(fetchShipments({ accessToken: keystoneInterfaceAccessToken }));
            } else if (page === "payouts") {
                dispatch(fetchPayouts({ accessToken: keystoneInterfaceAccessToken }));
            } else if (page === "team") {
                dispatch(fetchTeam({ accessToken: keystoneInterfaceAccessToken }));
            } else if (page === "offers") {
                dispatch(fetchOffers({ accessToken: keystoneInterfaceAccessToken }));
            }
        }
    }, [page, keystoneInterfaceAccessToken, dispatch]);

    useEffect(() => {
        if (keystoneInterfaceErrorFetchingInventory === 'Unauthorized') {
            handleLogout();
        }
    }, [keystoneInterfaceErrorFetchingInventory]);

    useEffect(() => {
        if (keystoneErrorFetchingAccessToken && !keystoneInterfaceAccessToken && !user && !isLoading) {
            handleLogout();
        }
    }, [keystoneErrorFetchingAccessToken, keystoneInterfaceAccessToken, user, isLoading]);

    useEffect(() => {
        if (keystoneInterfaceHasFetchedInventory && !keystoneInterfaceAccessToken && keystoneTemporaryAccessToken && fontsLoaded) {
            dispatch(setAccessToken(keystoneTemporaryAccessToken));
        }
    }, [keystoneInterfaceHasFetchedInventory, keystoneInterfaceAccessToken, keystoneTemporaryAccessToken, dispatch, fontsLoaded]);

    useEffect(() => {
        if (!keystoneInterfaceAccessToken) {
            if (keystoneTemporaryAccessToken) {
                dispatch(fetchInventory({ accessToken: keystoneTemporaryAccessToken }));
            }
        }
    }, [keystoneInterfaceAccessToken, keystoneTemporaryAccessToken, dispatch]);

    useEffect(() => { 
        if (keystoneInterfaceAccessToken && keystoneInterfaceCompany && keystoneUser) {
            setShowLoader(false);
        }
    }, [keystoneInterfaceAccessToken, keystoneInterfaceCompany, keystoneUser]);

    useEffect(() => {
        if ((!keystoneInterfaceCompany || !keystoneUser) && keystoneInterfaceAccessToken) {
            fetchUserAndCompany();
        }
    }, [keystoneInterfaceAccessToken, keystoneInterfaceCompany, keystoneUser]);

    // TODO: Address polling
    useEffect(() => {
        let intervalId;
      
        if (keystoneInterfaceAccessToken && page === "inventory") {
            if (keystoneInventoryAll?.length === 0) {
                dispatch(fetchInventory({ accessToken: keystoneInterfaceAccessToken }));
            }
            
            intervalId = setInterval(() => {
                if (keystoneInterfaceAccessToken && page === "inventory") {
                    dispatch(fetchInventory({ accessToken: keystoneInterfaceAccessToken }));
                }
            }, 10000);
        }
      
        return () => {
          if (intervalId) {
            clearInterval(intervalId);
          }
        };
    }, [dispatch, keystoneInterfaceAccessToken, page, keystoneInventoryAll?.length]);
    
    useEffect(() => {
        if (keystoneInterfaceModalType === 'feedback' && keystoneInterfaceFeedbackSubmitted) {
            dispatch(setShowModal(false));
            dispatch(setFeedbackSubmitted(false));
        }
    }, [keystoneInterfaceFeedbackSubmitted, keystoneInterfaceModalType, dispatch]);

    const handleModalDismiss = () => {
        dispatch(setShowModal(false));
        if (keystoneInterfaceModalType?.includes('upload-flow')) {
            dispatch(setOperationId(null));
            dispatch(setUploadedInventory(null));
            dispatch(setErrorUploadingInventory(null));
            dispatch(setValidationErrors(null));
            dispatch(setCommittedInventory(null));
            dispatch(setHeaderMappings(null));
        } else if (keystoneInterfaceModalType === "set-warehouse-address") {
            dispatch(setSetWarehouseLocationsSuccessfully(false));
            dispatch(setErrorSettingWarehouseLocations(null));
            dispatch(setSettingWarehouseLocations(false));
        }
    };

    const usersName =  useMemo(() => {
        return keystoneInterfaceModalType === "delete-user" ?
            (keystoneUserToDelete?.first_name ? `${keystoneUserToDelete?.first_name} ${keystoneUserToDelete?.last_name}` : keystoneUserToDelete?.email) :
            keystoneInterfaceModalType === "change-user-role" ?
            (keystoneUserToChange?.first_name ? `${keystoneUserToChange?.first_name} ${keystoneUserToChange?.last_name}` : keystoneUserToChange?.email) :
            "";
    }, [keystoneUserToDelete, keystoneUserToChange, keystoneInterfaceModalType]);
    
    return (
        <div id="keystone">
            <script src="https://cdn.plaid.com/link/v2/stable/link-initialize.js"></script>
            <div id="content-wrapper" className={menuOpen && 'menu-open'}>
                <Menu
                    menuOpen={menuOpen}
                    setMenuOpen={setMenuOpen}
                    className={menuOpen ? 'open' : 'closed'}
                    handleLogout={() => handleLogout()}
                ></Menu>
                <Content></Content>
                <Menu
                    menuOpen={menuOpen}
                    setMenuOpen={setMenuOpen}
                    className={menuOpen ? 'open' : 'closed'}
                    handleLogout={() => handleLogout()}
                ></Menu>
            </div>
            <Tray></Tray>
            {(keystoneInterfaceShowModal && keystoneInterfaceModalType === "box-minimum") ? (
                <Modal
                    title="Minimum Shipment Value"
                    onClose={handleModalDismiss}
                    allowDismiss={false}>
                    <BoxMinimumModal />
                </Modal>
            ) : (keystoneInterfaceShowModal && keystoneInterfaceModalType === "invite-member") ? (
                <Modal
                    title="Invite Member"
                    onClose={handleModalDismiss}
                    allowDismiss={false}>
                    <InviteModal />
                </Modal>
            ) : (keystoneInterfaceShowModal && keystoneInterfaceModalType === "set-warehouse-address") ? (
                <Modal
                    title="Edit Warehouse Location"
                    onClose={handleModalDismiss}>
                    <SetWarehouseLocationModal handleDimiss={handleModalDismiss} />
                </Modal>
            ) : (keystoneInterfaceShowModal && keystoneInterfaceModalType === "change-user-role") ? (
                <Modal
                    title={keystoneInventoryTeamMembers?.filter(member => member.role === "ADMIN")?.length === 1 && (keystoneInventoryTeamMembers?.filter(member => member.role === "ADMIN")?.[0]?.email === keystoneUserToChange?.email) ? "Cannot Change User Role" : "Change User Role"}
                    onClose={handleModalDismiss}>
                    <ConfirmModal
                        contextText={`Are you sure you want to change ${usersName}'s role to ${keystoneRoleToChange === "VIEWER" ? "Viewer" : "Admin"}?`}
                        contextTextDisabled={`You cannot change ${usersName}'s role because they are the only Admin in the organization.`}
                        disabled={keystoneInventoryTeamMembers?.filter(member => member.role === "ADMIN")?.length === 1 && (keystoneInventoryTeamMembers?.filter(member => member.role === "ADMIN")?.[0]?.email === keystoneUserToChange?.email)}
                        primaryButtonText="Yes, Change Role"
                        primaryButtonDisabledText="Ok"
                        secondaryButtonText="No, Cancel" />
                </Modal>
            ) : (keystoneInterfaceShowModal && keystoneInterfaceModalType === "feedback") ? (
                <Modal
                    title="Give Feedback"
                    onClose={handleModalDismiss}
                    allowDismiss={false}>
                    <TextAreaModal
                        cta="Send Feedback"
                        inputLabel="Feedback:"
                        inputPlaceholder="Enter your feedback here"
                        onSubmit={(feedback) => dispatch(submitFeedback({ feedback, accessToken: keystoneInterfaceAccessToken }))}
                        ctaLoading={keystoneInterfaceSubmittingFeedback}/>
                </Modal>
            ) : (keystoneInterfaceShowModal && keystoneInterfaceModalType === "delete-user") ? (
                <Modal
                    title={keystoneInventoryTeamMembers?.filter(member => member.role === "ADMIN")?.length === 1 && (keystoneInventoryTeamMembers?.filter(member => member.role === "ADMIN")?.[0]?.email === keystoneUserToDelete?.email) ? "Cannot Delete User" : "Delete User"}
                    onClose={handleModalDismiss}
                    allowDismiss={false}>
                    <ConfirmModal
                        contextText={`Are you sure you want to remove ${usersName} from your organization?`}
                        contextTextDisabled={`You cannot delete ${usersName} because they are the only Admin in the organization.`}
                        disabled={keystoneInventoryTeamMembers?.filter(member => member.role === "ADMIN")?.length === 1 && (keystoneInventoryTeamMembers?.filter(member => member.role === "ADMIN")?.[0]?.email === keystoneUserToDelete?.email)}
                        primaryButtonText="Yes, Delete User"
                        primaryButtonDisabledText="Ok"
                        secondaryButtonText="No, Cancel" />
                </Modal>
            ) : (keystoneInterfaceShowModal && keystoneInterfaceModalType === "tos") ? (
                <Modal
                    title="Terms of Use"
                    allowDismiss={false}
                    allowEscapeDismiss={false}
                    showBlur={true}>
                    <TermsAgreement />
                </Modal>
            ) : (keystoneInterfaceShowModal && keystoneInterfaceModalType.includes("upload-flow")) ? (
                <Modal
                    variant="unstyled"
                    size={(uploadStep === 0 && !keystoneInterfaceIsLoggedIn) ? "large" : "normal"}
                    allowDismiss={false}
                    allowEscapeDismiss={false}
                    showBlur={true}>
                    <UploadFlow
                        isUpdate={keystoneInterfaceModalType === "upload-flow-file-update"}
                        handleDismiss={handleModalDismiss}
                        step={uploadStep}
                        setStep={setUploadStep} />
                </Modal>
            ) : (keystoneInterfaceShowModal && keystoneInterfaceModalType.includes("create-account")) ? (
                <Modal
                    variant="unstyled"
                    size={keystoneInterfaceModalType === "create-account-invite" ? "small" : "large"}
                    allowDismiss={keystoneInterfaceModalType === "create-account-invite" ? false : true}
                    allowEscapeDismiss={keystoneInterfaceModalType === "create-account-invite" ? false : true}
                    showBlur={keystoneInterfaceModalType === "create-account-invite" ? true : false}
                    onClose={handleModalDismiss}>
                    <CreateAccount
                        handleCredentialAccountCreate={handleCredentialAccountCreate}
                        handleCredentialAccountCreateFromInvite={handleCredentialAccountCreateFromInvite}
                        handleGoogleAccountCreate={handleGoogleAccountCreate}
                        handleGoogleAccountCreateFromInvite={handleGoogleAccountCreateFromInvite} />
                </Modal>
            ) : null}
            <Loader loading={showLoader} showLoadingText={showLoadingText} loadingTextOptions={loadingTextOptions} />
            <Plaid />
            <ToastWrapper />
        </div>
    );
};

export default React.memo(Keystone);
